import React, { createContext, useContext, useState } from 'react';
import { toast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';

export interface WishlistItem {
  id: string;
  name: string;
  price: number;
  originalPrice: number;
  image: string;
  badge?: string;
  category?: string;
  categorySlug?: string;
  productSlug?: string;
  rating: number;
  description?: string;
}

interface WishlistContextType {
  wishlist: WishlistItem[];
  addToWishlist: (item: WishlistItem, onAuthRequired?: () => void) => void;
  removeFromWishlist: (id: string, silent?: boolean) => void;
  isInWishlist: (id: string) => boolean;
  wishlistCount: number;
}

const WishlistContext = createContext<WishlistContextType | undefined>(undefined);

export const WishlistProvider = ({ children }: { children: React.ReactNode }) => {
  const [wishlist, setWishlist] = useState<WishlistItem[]>([]);
  const { isAuthenticated } = useAuth();

  const addToWishlist = (item: WishlistItem, onAuthRequired?: () => void) => {
    if (!isAuthenticated) {
      toast({
        title: "Login Required",
        description: "Please login to add items to your wishlist",
      });
      onAuthRequired?.();
      return;
    }

    setWishlist(prevWishlist => {
      const exists = prevWishlist.find(wishlistItem => wishlistItem.id === item.id);
      
      if (exists) {
        toast({
          title: "Already in wishlist",
          description: `${item.name} is already in your wishlist`,
        });
        return prevWishlist;
      }
      
      toast({
        title: "Added to wishlist",
        description: `${item.name} has been added to your wishlist`,
      });
      return [...prevWishlist, item];
    });
  };

  const removeFromWishlist = (id: string, silent = false) => {
    setWishlist(prevWishlist => prevWishlist.filter(item => item.id !== id));
    if (!silent) {
      toast({
        title: "Removed from wishlist",
        description: "Item has been removed from your wishlist",
      });
    }
  };

  const isInWishlist = (id: string) => {
    return wishlist.some(item => item.id === id);
  };

  const wishlistCount = wishlist.length;

  return (
    <WishlistContext.Provider value={{ 
      wishlist, 
      addToWishlist, 
      removeFromWishlist, 
      isInWishlist,
      wishlistCount 
    }}>
      {children}
    </WishlistContext.Provider>
  );
};

// Default values for when context is not available (prevents crash during initialization)
const defaultWishlistContext: WishlistContextType = {
  wishlist: [],
  addToWishlist: () => {},
  removeFromWishlist: () => {},
  isInWishlist: () => false,
  wishlistCount: 0,
};

export const useWishlist = () => {
  const context = useContext(WishlistContext);
  if (!context) {
    console.warn('useWishlist called outside WishlistProvider, returning default values');
    return defaultWishlistContext;
  }
  return context;
};
